import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { cn } from "utils/helpers";
import { scopeTypeStyle } from "newui/features/RolesPermissions";
import type {
	AssignmentPlanBlocked,
	AssignmentPlanForUser,
	AssignmentPlanItem,
	AssignmentPlanRemoval,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

export interface AccessConfirmModalProps {
	isOpen: boolean;
	isLoadingPreview: boolean;
	previewError: string | null;
	// CONTRACT §11.4 — rendered VERBATIM from previewAssignment()'s response. This
	// component must never compute its own summary from selectedScopes.
	planForUsers: AssignmentPlanForUser[];
	/**
	 * CONTRACT §11.3a — top level on `preview` only, true iff at least one user has a non-empty
	 * `to_create`/`to_remove`. §11.4 addendum (A14): Confirm MUST NOT be presented as actionable
	 * when this is false — gated on the field itself, never re-derived by counting empty arrays
	 * (a plan can be all-`blocked`/`no_ops` and superficially look "checkable" if you only look at
	 * array lengths without this flag).
	 */
	actionable: boolean;
	isApplying: boolean;
	/**
	 * P0(b) — server-supplied `failures[].message` values from the apply-time atomic-failure
	 * response (CONTRACT §11.3/§6's same shape), extracted by the caller's `applyFailureMessages`
	 * (`AddAccessFlow.tsx`) — the SAME pattern as `DeleteConfirmModal`'s bulk-revoke banner. Empty
	 * array = no error. Rendered inline and persistently, not as a toast.
	 */
	applyErrorMessages: string[];
	/**
	 * Author item 5.2 — names the role this flow is adding, so the confirm screen reads "Adding
	 * Procurement Lead access" rather than leaving the admin to remember what they picked two
	 * steps ago. `null` while unresolved (should not happen by the time this dialog is open, but
	 * a missing name should degrade to "this role" rather than blank/crash).
	 */
	roleName: string | null;
	/** No user-name lookup exists in this flow's data yet (D10 wires the picker's
	 * user objects through) — defaults to a stable, honest placeholder. */
	userLabel?: (userId: number) => string;
	onBack: () => void;
	onCancel: () => void;
	onConfirm: () => void;
}

const defaultUserLabel = (userId: number): string => `User #${userId}`;

const planItemKey = (item: AssignmentPlanItem | AssignmentPlanBlocked): string =>
	`${item.scope_type}:${item.scope_id}`;

// D8-T3 — the final "confirm the access you're granting" screen
// (`data-testid="access-confirm-modal"`). Calls previewAssignment() (done by
// the caller, AddAccessFlow) and renders that response exactly: "Adding"
// (to_create, green), "Removing" (to_remove, R1's auto-cleanup, amber —
// deliberately NOT the same green as adds, per CONTRACT §11.4), a "Blocked"
// group (`blocked`, orange — CONTRACT §11.3a/§11.4 addendum, A14: neither an
// addition nor a removal, the server refused these before they became
// creatable, and their `message` is rendered verbatim, never dropped), and a
// collapsed "N already had this" disclosure (no_ops, R2). On Confirm, the
// caller re-sends the SAME frozen request to applyAssignment() — this
// component never rebuilds or re-derives it.
export const AccessConfirmModal = ({
	isOpen,
	isLoadingPreview,
	previewError,
	planForUsers,
	actionable,
	isApplying,
	applyErrorMessages,
	roleName,
	userLabel = defaultUserLabel,
	onBack,
	onCancel,
	onConfirm,
}: AccessConfirmModalProps) => {
	const showPerUserHeaders = planForUsers.length > 1;
	const totalNoOps = planForUsers.reduce((sum, plan) => sum + plan.no_ops.length, 0);
	// Author item 5.2/Obligation 2 — gated on the server's own `actionable` flag (never
	// re-derived by counting to_create/to_remove ourselves — CONTRACT §11.3a is explicit that the
	// client must not have to do that arithmetic).
	const canConfirm = !isLoadingPreview && !previewError && !isApplying && actionable;
	const showNotActionableNotice =
		!isLoadingPreview && !previewError && !actionable && planForUsers.length > 0;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onCancel}
			title="Confirm access changes"
			modalType="standard"
			iconType="info"
			closeOnEsc
			customFooter={
				// Author item 5.1 — Cancel is removed entirely from this footer: Back already
				// returns to the previous step (which is what Cancel here amounted to for anyone
				// who hadn't actually decided to abandon the whole flow), and the modal's own
				// close affordance (X / Escape, still wired to `onCancel`) covers "abandon
				// entirely". A second, redundant "Cancel" invited exactly the wrong keystroke this
				// close to a real Confirm.
				<div className="flex w-full justify-between gap-2">
					<Button btntype="plain-border" size="sm" onClick={onBack} data-testid="wizard-back">
						Back
					</Button>
					<Button
						btntype="primary"
						size="sm"
						onClick={onConfirm}
						disabled={!canConfirm}
						isLoading={isApplying}
						data-testid="wizard-confirm"
					>
						Confirm
					</Button>
				</div>
			}
		>
			<div data-testid="access-confirm-modal" className="space-y-4">
				{/* Author item 5.2 — names the role being added, so the confirm screen doesn't
				    make the admin remember what they picked two steps ago. */}
				{roleName && (
					<p className="text-sm text-gray-700" data-testid="access-confirm-role-name">
						Adding <span className="font-semibold">{roleName}</span> access
					</p>
				)}

				{isLoadingPreview && (
					<p className="text-sm text-gray-500" data-testid="access-confirm-loading">
						Working out what will change…
					</p>
				)}

				{previewError && !isLoadingPreview && (
					<p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="access-confirm-error">
						{previewError}
					</p>
				)}

				{applyErrorMessages.length > 0 && (
					<div
						role="alert"
						className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
						data-testid="access-confirm-apply-error"
					>
						<p className="font-semibold">This access could not be applied. Nothing was changed.</p>
						<ul className="mt-1 list-disc pl-4">
							{applyErrorMessages.map((message) => (
								<li key={message}>{message}</li>
							))}
						</ul>
					</div>
				)}

				{showNotActionableNotice && (
					<p
						className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
						data-testid="access-confirm-not-actionable"
					>
						Nothing to apply — every requested access is either already held or blocked (see
						below).
					</p>
				)}

				{!isLoadingPreview &&
					!previewError &&
					planForUsers.map((plan) => (
						<div key={plan.user_id} className="space-y-3">
							{showPerUserHeaders && (
								<h4 className="text-sm font-semibold text-gray-800">{userLabel(plan.user_id)}</h4>
							)}

							{plan.to_create.length > 0 && (
								<PlanList
									items={plan.to_create}
									heading="Adding"
									tone="add"
									testId={
										showPerUserHeaders
											? `access-confirm-added-${plan.user_id}`
											: "access-confirm-added"
									}
								/>
							)}

							{plan.to_remove.length > 0 && (
								<PlanList
									items={plan.to_remove}
									heading="Removing"
									tone="remove"
									testId={
										showPerUserHeaders
											? `access-confirm-removed-${plan.user_id}`
											: "access-confirm-removed"
									}
								/>
							)}

							{plan.blocked.length > 0 && (
								<BlockedList
									items={plan.blocked}
									testId={
										showPerUserHeaders
											? `access-confirm-blocked-${plan.user_id}`
											: "access-confirm-blocked"
									}
								/>
							)}

							{plan.to_create.length === 0 &&
								plan.to_remove.length === 0 &&
								plan.blocked.length === 0 && (
									<p className="text-xs text-gray-500">No changes for this user.</p>
								)}
						</div>
					))}

				{!isLoadingPreview && !previewError && totalNoOps > 0 && (
					<details data-testid="access-confirm-noops-disclosure" className="text-xs text-gray-500">
						<summary className="cursor-pointer select-none font-medium text-gray-600">
							{totalNoOps} already had this
						</summary>
						<ul className="mt-2 list-inside list-disc space-y-1">
							{planForUsers.flatMap((plan) =>
								plan.no_ops.map((item) => (
									<li key={`${plan.user_id}-${planItemKey(item)}`}>
										{showPerUserHeaders ? `${userLabel(plan.user_id)} — ` : ""}
										{item.scope_label ?? "Company Wide"}
									</li>
								))
							)}
						</ul>
					</details>
				)}
			</div>
		</Modal>
	);
};

interface PlanListProps {
	items: AssignmentPlanItem[] | AssignmentPlanRemoval[];
	heading: string;
	tone: "add" | "remove";
	testId: string;
}

// Adding is green, Removing is amber/warning-toned — deliberately distinct so
// a removal is never mistaken for an addition while scanning (CONTRACT §11.4).
const PlanList = ({ items, heading, tone, testId }: PlanListProps) => (
	<div
		data-testid={testId}
		className={cn(
			"rounded-md border px-3 py-2",
			tone === "add" ? "border-emerald-200 bg-emerald-50" : "border-amber-300 bg-amber-50"
		)}
	>
		<p
			className={cn(
				"mb-1 text-[11px] font-semibold uppercase tracking-wide",
				tone === "add" ? "text-emerald-800" : "text-amber-900"
			)}
		>
			{heading} ({items.length})
		</p>
		<ul className={cn("space-y-0.5 text-sm", tone === "add" ? "text-emerald-900" : "text-amber-900")}>
			{items.map((item) => (
				<li key={planItemKey(item)}>
					{/* Author item 5.3 — for specific access, name the scope TYPE (Portfolio/Project/
					    RFx), not just the item's own name — a plain item label alone doesn't say
					    what KIND of grant this is. `global` is skipped: its own label already reads
					    "Company Wide", and a "Company Wide" badge next to "Company Wide" text is
					    just noise. */}
					{item.scope_type !== "global" && (
						<span
							className={cn(
								"mr-1.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
								scopeTypeStyle(item.scope_type).chip
							)}
						>
							{scopeTypeStyle(item.scope_type).label}
						</span>
					)}
					{item.scope_label ?? "Company Wide"}
				</li>
			))}
		</ul>
	</div>
);

interface BlockedListProps {
	items: AssignmentPlanBlocked[];
	testId: string;
}

// CONTRACT §11.3a/§11.4 addendum (A14) — a THIRD group, neither an addition nor a removal: rows
// `assertGrantable()` rejected before they could ever become creatable. `message` is rendered
// VERBATIM ("load-bearing" per the coordinator) — the amendment's narrowing of all-or-nothing
// atomicity is only defensible because the admin is told exactly what was skipped and why, here.
const BlockedList = ({ items, testId }: BlockedListProps) => (
	<div
		data-testid={testId}
		role="alert"
		className="rounded-md border border-orange-300 bg-orange-50 px-3 py-2"
	>
		<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-orange-900">
			Blocked ({items.length})
		</p>
		<ul className="space-y-0.5 text-sm text-orange-900">
			{items.map((item) => (
				<li key={planItemKey(item)}>
					<span className="font-medium">{item.scope_label ?? "Company Wide"}</span>
					{" — "}
					{item.message}
				</li>
			))}
		</ul>
	</div>
);
